import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { 
  LayoutDashboard, 
  Users, 
  Phone, 
  Calendar, 
  Target, 
  TrendingUp, 
  Clock, 
  FileText,
  UserCheck
} from 'lucide-react';

const SalesDashboard = () => {
  const { user, isAuthenticated, isSales } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || (!isSales && !user?.role === 'admin')) {
      navigate('/login-crm');
    }
  }, [isAuthenticated, isSales, user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <Header />
      
      <div className="flex-grow container mx-auto px-4 pt-48 pb-8">
        {/* Welcome Section with Stats */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-8 mb-8 text-white">
          <h1 className="text-3xl font-bold mb-6">Benvenuto, {user.nome} {user.cognome}!</h1>
          
          {/* Stats Row */}
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {/* INFLOW Personale Mese */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <p className="text-sm text-green-100 mb-2">INFLOW Personale (Mese)</p>
              <p className="text-3xl font-bold">€ 0,00</p>
              <p className="text-xs text-green-200 mt-2">Contratti chiusi questo mese</p>
            </div>
            
            {/* INFLOW Totale Team */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <p className="text-sm text-green-100 mb-2">INFLOW Totale Team (Mese)</p>
              <p className="text-3xl font-bold">€ 0,00</p>
              <p className="text-xs text-green-200 mt-2">Tutti i venditori - mese corrente</p>
            </div>
            
            {/* Pagamento Personale */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <p className="text-sm text-green-100 mb-2">Pagamento Personale (Mese)</p>
              <p className="text-3xl font-bold">€ 0,00</p>
              <p className="text-xs text-green-200 mt-2">Ammontare contratti chiusi</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Lead Assegnati</h3>
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-xs text-gray-500 mt-2">Da implementare</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Chiamate Oggi</h3>
              <Phone className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-xs text-gray-500 mt-2">Da implementare</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Opportunità</h3>
              <Target className="w-8 h-8 text-orange-400" />
            </div>
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-xs text-gray-500 mt-2">Da implementare</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Attivazioni</h3>
              <UserCheck className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-xs text-gray-500 mt-2">Da implementare</p>
          </div>
        </div>

        {/* Dashboard Sections */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Lead Management */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Users className="w-6 h-6 mr-2 text-blue-400" />
              Gestione Lead
            </h2>
            <div className="space-y-3">
              <button className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
                <h3 className="font-semibold text-white mb-1">Nuovi Lead</h3>
                <p className="text-sm text-gray-400">Lead da contattare (0)</p>
              </button>
              <button className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
                <h3 className="font-semibold text-white mb-1">In Lavorazione</h3>
                <p className="text-sm text-gray-400">Lead in fase di negoziazione (0)</p>
              </button>
              <button className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
                <h3 className="font-semibold text-white mb-1">Da Ricontattare</h3>
                <p className="text-sm text-gray-400">Follow-up programmati (0)</p>
              </button>
            </div>
          </div>

          {/* Task Management */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Calendar className="w-6 h-6 mr-2 text-green-400" />
              Task & Appuntamenti
            </h2>
            <div className="space-y-3">
              <button className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
                <h3 className="font-semibold text-white mb-1">Appuntamenti Oggi</h3>
                <p className="text-sm text-gray-400">Nessun appuntamento</p>
              </button>
              <button className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
                <h3 className="font-semibold text-white mb-1">Chiamate da Fare</h3>
                <p className="text-sm text-gray-400">Lista chiamate vuota</p>
              </button>
              <button className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors duration-200">
                <h3 className="font-semibold text-white mb-1">Task Priorità Alta</h3>
                <p className="text-sm text-gray-400">Nessun task urgente</p>
              </button>
            </div>
          </div>
        </div>

        {/* Sales Pipeline */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <TrendingUp className="w-6 h-6 mr-2 text-orange-400" />
            Pipeline Vendite
          </h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Lead</h3>
              <p className="text-2xl font-bold text-blue-400">0</p>
              <p className="text-xs text-gray-400 mt-1">Nuovi contatti</p>
            </div>
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Qualificati</h3>
              <p className="text-2xl font-bold text-green-400">0</p>
              <p className="text-xs text-gray-400 mt-1">In valutazione</p>
            </div>
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Proposta</h3>
              <p className="text-2xl font-bold text-orange-400">0</p>
              <p className="text-xs text-gray-400 mt-1">Preventivo inviato</p>
            </div>
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="font-semibold text-white mb-2">Chiuso</h3>
              <p className="text-2xl font-bold text-purple-400">0</p>
              <p className="text-xs text-gray-400 mt-1">Contratto firmato</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <button
            onClick={() => navigate('/lead/nuovo')}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white p-8 rounded-xl transition-all duration-200 flex items-center justify-center space-x-3 shadow-lg"
          >
            <Users size={32} />
            <div className="text-left">
              <h3 className="text-xl font-bold">Crea Nuovo Lead</h3>
              <p className="text-sm text-green-100">Aggiungi contatto e prodotti</p>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/leads/pipeline')}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white p-8 rounded-xl transition-all duration-200 flex items-center justify-center space-x-3 shadow-lg"
          >
            <FileText size={32} />
            <div className="text-left">
              <h3 className="text-xl font-bold">Vedi Pipeline</h3>
              <p className="text-sm text-blue-100">Gestisci tutti i lead</p>
            </div>
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-green-500/10 border border-green-500/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-green-400 mb-2">🎯 Dashboard Sales Attiva!</h3>
          <p className="text-gray-300">
            Clicca su "Crea Nuovo Lead" per aggiungere un nuovo contatto e associare prodotti con calcolo automatico di INFLOW e pagamenti agente.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SalesDashboard;